import { useEffect, useRef, useState } from 'react';
import { Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle, MenuItem, Paper, Stack, Tab, Tabs, TextField, Tooltip, Typography } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { CartesianGrid, ComposedChart, Line, Bar, BarChart, Legend, ResponsiveContainer, Tooltip as ChartTooltip, XAxis, YAxis } from 'recharts';
import { pmApi, labels, valueLabels } from './projectBaseApi';
import { projectSchedule, scheduleDate, sessionStatus, sessionTime } from './projectSchedule';
const formatDate = d => d ? new Date(d + 'T00:00:00Z').toLocaleDateString('es-BO', {
  timeZone: 'UTC',
  day: '2-digit',
  month: 'short'
}) : 'Sin fecha';
const day = d => Date.parse(d + 'T00:00:00Z') / 86400000;
const stages = [['pending', 'Pendiente'], ['ready', 'Listo'], ['doing', 'En ejecución'], ['review', 'En revisión'], ['completed', 'Terminado']];
const viewNames = [['gantt', 'Gantt'], ['kanban', 'Kanban'], ['load', 'Mapa de carga'], ['variance', 'Plan frente a real'], ['trends', 'Tendencias']];
function Metric({
  name,
  value,
  detail
}) {
  return <Paper variant="outlined" sx={{
    p: 2
  }}><Typography variant="caption" color="text.secondary">{name}</Typography><Typography variant="h5" my={0.5}>{value ?? 'Sin registro'}</Typography><Typography variant="caption" color="text.secondary">{detail}</Typography></Paper>;
}
function Gantt({
  data,
  records,
  onFocus,
  onTrace
}) {
  const theme = useTheme();
  const [exporting, setExporting] = useState('');
  const [exportError, setExportError] = useState('');
  const schedule = projectSchedule(data, records);
  const download = async kind => {
    setExporting(kind);
    setExportError('');
    try {
      const { downloadProjectPdf } = await import('./projectSchedulePdf');
      await downloadProjectPdf(kind, data, records);
    } catch (error) {
      setExportError('No se pudo descargar el PDF. Vuelve a intentarlo.');
    } finally {
      setExporting('');
    }
  };
  const {
    from,
    to
  } = data.filters;
  const width = 1100,
    left = 290,
    right = 30,
    range = Math.max(1, day(to) - day(from) + 1),
    plot = width - left - right,
    x = d => left + Math.max(0, Math.min(range, day(d) - day(from))) * plot / range;
  const height = 90 + data.timeline.length * 68;
  const visible = data.timeline;
  return <Paper variant="outlined" sx={{
    p: 3
  }}><Typography variant="h6">Fechas, sesiones y dependencias</Typography><Typography color="text.secondary" variant="body2" mb={2}>Selecciona una actividad para seguirla en las cinco vistas. Las dependencias son vínculos registrados; no se presentan como una ruta crítica calculada.</Typography>
 <Stack direction="row" gap={1} flexWrap="wrap" mb={1}>
   <Button variant="outlined" disabled={!visible.length || Boolean(exporting)} onClick={() => download('gantt')}>{exporting === 'gantt' ? 'Generando Gantt…' : 'Descargar Gantt en PDF'}</Button>
   <Button variant="outlined" disabled={!visible.length || Boolean(exporting)} onClick={() => download('schedule')}>{exporting === 'schedule' ? 'Generando cronograma…' : 'Descargar cronograma en PDF'}</Button>
 </Stack>
 <Typography variant="caption" color="text.secondary" display="block" mb={2}>Los PDF incluyen todas las filas de la selección y el periodo actual, distribuidas en páginas cuando sea necesario.</Typography>
 {exportError && <Alert severity="error" sx={{ mb: 2 }}>{exportError}</Alert>}
 <Stack direction="row" gap={2} flexWrap="wrap" mb={2}><Chip size="small" variant="outlined" label="Línea discontinua: plan aprobado" /><Chip size="small" color="primary" label="Azul: planificación actual" /><Chip size="small" color="success" label="Verde: ejecución registrada" /><Chip size="small" variant="outlined" label="Diamantes: compromisos de tareas" /></Stack>
 {!visible.length ? <Alert severity="info">Registra sesiones con fechas para visualizar el cronograma.</Alert> : <Box sx={{
      overflowX: 'auto'
    }}><svg role="img" aria-label="Gantt: actividades por fecha, plan aprobado y ejecución real" viewBox={`0 0 ${width} ${height}`} style={{
        width: '100%',
        minWidth: 850,
        display: 'block'
      }}><text x={12} y={20} fill={theme.palette.text.secondary} fontSize={12}>Actividad / resultado esperado</text><text x={left} y={20} fill={theme.palette.text.secondary} fontSize={12}>Fecha · Bolivia</text>{data.weeks.map((w, i) => <g key={w.start}><line x1={x(w.start)} x2={x(w.start)} y1={45} y2={height - 25} stroke={theme.palette.divider} />{i % Math.max(1, Math.ceil(data.weeks.length / 12)) === 0 && <text x={x(w.start)} y={40} fontSize={11} fill={theme.palette.text.secondary}>{formatDate(w.start)}</text>}</g>)}
 {visible.map((r, i) => {
          const y = 65 + i * 68;
          return <g key={r.id}><line x1={0} x2={width} y1={y + 52} y2={y + 52} stroke={theme.palette.divider} /><g role="button" tabIndex={0} aria-label={`Seleccionar ${r.name}`} onClick={() => onFocus(r.id)} onKeyDown={e => {
              if (e.key === 'Enter' || e.key === ' ') onFocus(r.id);
            }} style={{
              cursor: 'pointer'
            }}><title>{r.name}: {r.outcome}</title><text x={12} y={y + 8} fill={theme.palette.text.primary} fontSize={13} fontWeight={600}>{r.name.length > 35 ? r.name.slice(0, 33) + '…' : r.name}</text><text x={12} y={y + 27} fill={theme.palette.text.secondary} fontSize={11}>{r.completed} ejecutadas · {r.sessions.length} programadas</text></g>{r.baseline && <rect x={x(r.baseline.start)} y={y - 5} width={Math.max(6, x(r.baseline.end) - x(r.baseline.start) + plot / range)} height={10} fill="none" stroke={theme.palette.text.secondary} strokeDasharray="4 3" />}{r.current && <rect x={x(r.current.start)} y={y + 9} width={Math.max(6, x(r.current.end) - x(r.current.start) + plot / range)} height={12} rx={3} fill={theme.palette.primary.main} opacity={0.75} />} {r.actual && <rect x={x(r.actual.start)} y={y + 26} width={Math.max(6, x(r.actual.end) - x(r.actual.start) + plot / range)} height={8} rx={3} fill={theme.palette.success.main} />}{r.sessions.map(s => <circle key={s.id} role="button" tabIndex={0} aria-label={`${r.name}: sesión ${s.date}`} cx={x(s.date) + 3} cy={y + 15} r={4} fill={theme.palette.primary.dark} onClick={() => onTrace([{
              kind: 'session',
              id: s.id,
              name: r.name + ' · ' + formatDate(s.date)
            }])} onKeyDown={e => {
              if (e.key === 'Enter') onTrace([{
                kind: 'session',
                id: s.id,
                name: r.name
              }]);
            }} style={{
              cursor: 'pointer'
            }}><title>{formatDate(s.date)} · {s.startTime}–{s.endTime} · {valueLabels[s.status]}</title></circle>)}{[...new Set((r.tasks||[]).map(t=>t.date))].map(date=>{const sameDay=r.tasks.filter(t=>t.date===date);const refs=sameDay.map(t=>({kind:'task',id:t.id,name:t.name}));return <g key={date} role="button" tabIndex={0} aria-label={sameDay.length+' tareas: '+date} onClick={()=>onTrace(refs)} onKeyDown={e=>{if(e.key==='Enter')onTrace(refs);}} style={{cursor:'pointer'}}><title>{sameDay.map(t=>t.name).join(' · ')}</title><rect x={x(date)-6} y={y+37} width={20} height={14} fill="transparent"/><path d={'M'+x(date)+' '+(y+40)+' l4 4 l-4 4 l-4 -4 Z'} fill={sameDay.every(t=>t.status==='completed')?theme.palette.success.main:theme.palette.warning.dark}/>{sameDay.length>1&&<text x={x(date)+7} y={y+47} fontSize={9} fill={theme.palette.text.secondary}>{sameDay.length}</text>}</g>;})}</g>;
        })}
 {visible.flatMap((r, i) => r.dependencies.map(id => {
          const previous = visible.findIndex(a => a.id === id);
          const p = visible[previous];
          if (previous < 0 || !p.current || !r.current) return null;
          const x1 = x(p.current.end),
            x2 = x(r.current.start),
            y1 = 80 + previous * 68,
            y2 = 80 + i * 68;
          return <path key={id + '-' + r.id} d={`M${x1} ${y1} H${x1 + 12} V${y2} H${x2}`} fill="none" stroke={theme.palette.warning.main} strokeDasharray="3 2"><title>{p.name} precede a {r.name}</title></path>;
        }))}
 {data.filters.asOf >= from && data.filters.asOf <= to && <g><line x1={x(data.filters.asOf)} x2={x(data.filters.asOf)} y1={45} y2={height - 25} stroke={theme.palette.error.main} strokeDasharray="6 4" /><text x={x(data.filters.asOf) + 5} y={height - 9} fill={theme.palette.error.main} fontSize={11}>Corte: {formatDate(data.filters.asOf)}</text></g>}</svg></Box>}
 <Typography variant="caption" color="text.secondary">Fuente: sesiones y planes aprobados del proyecto · {formatDate(from)}–{formatDate(to)}. Cada punto abre su registro.</Typography>
 <Box mt={3}><Typography variant="h6">Cronograma de actividades y sesiones</Typography>
 <Typography variant="body2" color="text.secondary" mb={2}>Inicio y fin corresponden a la primera y última sesión vigente dentro del periodo seleccionado: {scheduleDate(from)}–{scheduleDate(to)}. Horarios de Bolivia. Las sesiones canceladas no se incluyen.</Typography>
 {schedule.map(activity => <Box component="details" key={activity.id} sx={{ borderTop: 1, borderColor: 'divider', py: 1.5 }}>
   <Box component="summary" sx={{ cursor: 'pointer' }}><strong>{activity.name}</strong> · Inicia: {scheduleDate(activity.current?.start)} · Finaliza: {scheduleDate(activity.current?.end)} · {activity.sessions.length} sesiones</Box>
   {!activity.sessions.length ? <Typography mt={1}>Sin sesiones vigentes en este periodo.</Typography> : <Box sx={{ overflowX: 'auto', mt: 1 }}>
     <Box component="table" sx={{ width: '100%', borderCollapse: 'collapse', '& th, & td': { p: 1, textAlign: 'left', borderBottom: 1, borderColor: 'divider' } }}>
       <caption>Clases de {activity.name}</caption><thead><tr>{['Clase / sesión', 'Fecha', 'Horario', 'Lugar', 'Facilitador', 'Estado'].map(label => <th scope="col" key={label}>{label}</th>)}</tr></thead>
       <tbody>{activity.sessions.map((session, index) => <tr key={session.id}><th scope="row"><Button size="small" onClick={() => onTrace([{ kind: 'session', id: session.id, name: session.name || `Sesión ${index + 1}` }])}>{session.name || `Sesión ${index + 1}`}</Button></th><td>{scheduleDate(session.date)}</td><td>{sessionTime(session)}</td><td>{session.location || 'Por confirmar'}</td><td>{session.facilitator || 'Por confirmar'}</td><td>{sessionStatus(session)}</td></tr>)}</tbody>
     </Box>
   </Box>}
 </Box>)}
 </Box></Paper>;
}
function Kanban({
  data,
  busy,
  onMove,
  onEdit
}) {
  const [dragged, setDragged] = useState(null);
  const board = data.kanban;
  return <><Box className="pm-visual-metrics"><Metric name="Trabajo en curso del proyecto" value={`${board.wip} / ${board.limit}`} detail="El límite sigue incluyendo tareas fuera del filtro" /><Metric name="Bloqueos en esta selección" value={board.blocked} detail="Un bloqueo no libera capacidad" /><Metric name="Tareas finalizadas" value={board.completed} detail="Con fecha real hasta el corte" /><Metric name="Tiempo de ciclo mediano" value={board.medianCycleDays === null ? 'Sin registro' : board.medianCycleDays + ' días'} detail="Desde inicio real hasta finalización" /></Box>{board.wip > board.limit && <Alert severity="warning" sx={{
      mb: 2
    }}>El trabajo en curso supera el límite acordado. Revisa las excepciones antes de iniciar más tareas.</Alert>}<Box className="pm-kanban">{stages.map(([status, label]) => {
        const tasks = board.tasks.filter(t => t.status === status);
        return <Paper key={status} variant="outlined" sx={{
          p: 1.5,
          minHeight: 200
        }} onDragOver={e => e.preventDefault()} onDrop={e => {
          e.preventDefault();
          if (dragged && !busy) onMove(dragged, status);
          setDragged(null);
        }}><Stack direction="row" justifyContent="space-between" mb={2}><Typography variant="subtitle2">{label}</Typography><Chip size="small" label={tasks.length} /></Stack>{tasks.map(t => <Paper key={t.id} variant="outlined" draggable={!busy} onDragStart={() => setDragged(t)} onDragEnd={() => setDragged(null)} sx={{
            p: 1.5,
            mb: 1.5,
            bgcolor: 'background.default',
            cursor: busy ? 'default' : 'grab'
          }}><Typography variant="subtitle2">{t.name}</Typography><Typography variant="caption" color="text.secondary" display="block" mt={0.5}>{t.activityName} · {t.responsible || 'Responsable por confirmar'}</Typography><Typography variant="caption" display="block">Vence: {formatDate(t.dueDate)}{t.ageDays !== null ? ' · abierto ' + t.ageDays + ' días' : ''}</Typography>{t.blockedReason && <Alert severity="warning" sx={{
              mt: 1,
              p: 0.5
            }}>Bloqueado: {t.blockedReason}</Alert>}<Typography variant="caption" color="text.secondary" display="block" mt={1}>Termina cuando: {t.acceptance || 'criterio pendiente'}</Typography><Stack direction="row" gap={0.5} mt={1} flexWrap="wrap"><Button size="small" onClick={() => onEdit('task', t)}>Revisar</Button>{status !== 'completed' && <Button size="small" disabled={busy} onClick={() => onMove(t, status === 'pending' ? 'ready' : status === 'ready' ? 'doing' : 'completed')}>{status === 'pending' ? 'Preparada' : status === 'ready' ? 'Empezar' : 'Finalizar'}</Button>}</Stack></Paper>)}{!tasks.length && <Typography variant="caption" color="text.secondary">Sin tareas en este estado.</Typography>}</Paper>;
      })}</Box><Typography variant="caption" color="text.secondary" display="block" mt={2}>Arrastra o utiliza los botones. Finalizar abre el registro para comprobar evidencia. Fuente: tareas actuales del proyecto; historial conservado en cada cambio de estado.</Typography></>;
}
function LoadMap({
  data,
  onTrace,
  onEdit
}) {
  const theme = useTheme();
  const [mode, setMode] = useState('planned');
  return <Paper variant="outlined" sx={{
    p: 3
  }}><Stack direction={{
      xs: 'column',
      sm: 'row'
    }} justifyContent="space-between" gap={2} mb={2}><Box><Typography variant="h6">Reservas y capacidad por semana</Typography><Typography variant="body2" color="text.secondary">Incluye las reservas de todo el proyecto. Así, filtrar un curso no hace parecer libres recursos ocupados por otro.</Typography></Box><TextField select label="Mostrar" size="small" value={mode} onChange={e => setMode(e.target.value)} sx={{
        minWidth: 150
      }}><MenuItem value="planned">Planificado</MenuItem><MenuItem value="actual">Uso real</MenuItem></TextField></Stack>
 {!data.loads.length ? <Alert severity="info" action={<Button onClick={() => onEdit('resource')}>Registrar recurso</Button>}>Registra disponibilidad y asignaciones para identificar sobrecarga y capacidad libre.</Alert> : <Box sx={{
      overflowX: 'auto'
    }}><table className="pm-load-table"><caption>Recursos × semanas · fechas de inicio de semana. Cada recurso indica su unidad y criterio.</caption><thead><tr><th scope="col">Recurso / capacidad</th>{data.weeks.map(w => <th key={w.start} scope="col">{formatDate(w.start)}</th>)}</tr></thead><tbody>{data.loads.map(r => <tr key={r.id}><th scope="row"><Button size="small" onClick={() => onEdit('resource', r)}>{r.name}</Button><Typography variant="caption" display="block">{r.capacity} {r.unit === 'hours' ? 'h' : r.unit} · {r.basis}</Typography></th>{r.cells.map(c => {
              const value = mode === 'actual' ? c.actual : c.planned;
              const ratio = value === null ? null : r.capacity ? value / r.capacity : value > 0 ? 2 : 0;
              const color = ratio === null ? theme.palette.text.disabled : ratio > 1 ? theme.palette.error.main : ratio >= 0.8 ? theme.palette.warning.dark : theme.palette.text.primary;
              return <td key={c.week}><Tooltip title={`${r.name}: ${value === null ? 'sin registro real' : value + ' ' + r.unit}; capacidad ${r.capacity}. Aporte seleccionado: ${c.selected}.`}><button type="button" className="pm-load-cell" style={{
                    color,
                    background: ratio === null ? theme.palette.action.disabledBackground : ratio > 1 ? theme.palette.tint.danger : ratio >= 0.8 ? theme.palette.tint.warning : theme.palette.tint.neutral
                  }} onClick={() => onTrace(c.refs)} aria-label={`${r.name}, semana ${c.week}: ${value === null ? 'sin registro' : value + ' de ' + r.capacity}`}><strong>{value === null ? '—' : Math.round(value * 100) / 100}</strong><small>{value === null ? 'Sin registro' : r.capacity ? Math.round(ratio * 100) + '%' : value > 0 ? 'Sin capacidad' : 'Sin reserva'}</small></button></Tooltip></td>;
            })}</tr>)}</tbody></table></Box>}
 <Alert severity="info" sx={{
      mt: 2
    }}>Horas: carga semanal. Espacios/equipos: pico simultáneo. Materiales/dinero: consumo acumulado. Rojo indica sobrecarga; ámbar desde 80 % es una señal visual configurable en el diseño, no un umbral científico universal.</Alert></Paper>;
}
function Variance({
  data,
  onTrace
}) {
  const theme = useTheme();
  return <><Typography variant="h6" mb={1}>Qué se aprobó, qué se prevé y qué se registró</Typography><Typography color="text.secondary" variant="body2" mb={2}>Cada gráfico conserva su unidad. Los costos reales son importes registrados, con cobertura visible; no se presentan como valor ganado ni retorno del programa.</Typography><Box className="pm-variance-charts">{data.differences.map(metric => <Paper key={metric.key} variant="outlined" sx={{
        p: 2
      }}><Typography variant="subtitle1">{metric.label}</Typography><Typography variant="caption" color="text.secondary">Unidad: {metric.unit}</Typography><Box sx={{
          height: 240,
          minWidth: 0
        }}><ResponsiveContainer width="100%" height="100%"><BarChart data={[{
              name: metric.label,
              approved: metric.baseline,
              current: metric.current,
              actual: metric.actual
            }]} margin={{
              top: 15,
              right: 10,
              bottom: 25,
              left: 12
            }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="name" tick={{
                fontSize: 10
              }} /><YAxis label={{
                value: metric.unit,
                angle: -90,
                position: 'insideLeft'
              }} /><ChartTooltip /><Legend /><Bar dataKey="approved" name="Plan aprobado" fill={theme.palette.text.disabled} /><Bar dataKey="current" name="Plan actual" fill={theme.palette.primary.main} /><Bar dataKey="actual" name="Real conocido" fill={theme.palette.success.main} /></BarChart></ResponsiveContainer></Box><Typography variant="body2">Aprobado: {metric.baseline === null ? 'sin aprobar' : metric.baseline} · Actual: {Math.round(metric.current * 100) / 100} · Real: {metric.actual === null ? 'sin registrar' : Math.round(metric.actual * 100) / 100}</Typography>{metric.baseline !== null && <Typography variant="caption" color="text.secondary">Cambio del plan: {Math.round((metric.current - metric.baseline) * 100) / 100} {metric.unit}</Typography>}<Button size="small" onClick={() => onTrace(metric.refs)}>Revisar datos reales</Button></Paper>)}</Box><Alert severity="info" sx={{
      mt: 2
    }}>Costos reales conocidos: {data.coverage.realCostEntries} de {data.coverage.realCostExpected} partidas. Revisa las partidas ausentes antes de concluir que hay ahorro.</Alert></>;
}
function TrendPlot({
  title,
  unit,
  data,
  series,
  onTrace
}) {
  const theme = useTheme();
  const colors = [theme.palette.text.disabled, theme.palette.primary.main, theme.palette.success.main, theme.palette.warning.dark];
  return <Paper variant="outlined" sx={{
    p: 2
  }}><Typography variant="subtitle1">{title}</Typography><Box sx={{
      height: 280,
      minWidth: 0
    }}><ResponsiveContainer width="100%" height="100%"><ComposedChart data={data} onClick={state => {
          const index = Number(state?.activeTooltipIndex ?? state?.activeIndex);
        const point = Number.isInteger(index) && index >= 0 ? data[index] : data.find(row => row.week === state?.activeLabel);
        if (point) onTrace(point.refs);
        }} margin={{
          top: 18,
          right: 15,
          bottom: 30,
          left: 15
        }}><CartesianGrid strokeDasharray="3 3" vertical={false} /><XAxis dataKey="week" tickFormatter={formatDate} label={{
            value: 'Semana (fecha de inicio)',
            position: 'insideBottom',
            offset: -15
          }} tick={{
            fontSize: 10
          }} minTickGap={30} /><YAxis domain={unit === '%' ? [0, 100] : [0, 'auto']} label={{
            value: unit,
            angle: -90,
            position: 'insideLeft'
          }} /><ChartTooltip labelFormatter={formatDate} formatter={(value, name) => [value === null ? 'Sin registro' : Math.round(value * 100) / 100, name]} /><Legend verticalAlign="top" />{series.map(([key, name], i) => <Line key={key} dataKey={key} name={name} type="linear" stroke={colors[i]} strokeWidth={2} dot={false} connectNulls={false} />)}</ComposedChart></ResponsiveContainer></Box><Typography variant="caption" color="text.secondary">Fuente: registros del proyecto en el periodo seleccionado. Las interrupciones indican datos ausentes; selecciona una semana para revisar evidencia.</Typography></Paper>;
}
function Trends({
  data,
  onTrace,
  onEdit
}) {
  return <><Box className="pm-trend-charts"><TrendPlot title="Ejecución acumulada de sesiones" unit="Sesiones" data={data.trends} series={[["baseline", "Plan aprobado"], ["planned", "Plan actual"], ["executed", "Ejecución registrada"]]} onTrace={onTrace} /><TrendPlot title="Asistencia y cobertura semanal" unit="%" data={data.trends} series={[["coverage", "Cobertura del registro"], ["attendance", "Asistencia entre registros conocidos"]]} onTrace={onTrace} /><TrendPlot title="Beneficios comprobados por etapa" unit="Logros acumulados" data={data.trends} series={[["learning", "Aprendizaje"], ["application", "Aplicación"], ["outcome", "Resultado externo"]]} onTrace={onTrace} /><TrendPlot title="Tareas finalizadas por semana" unit="Tareas" data={data.trends} series={[["finishedTasks", "Finalizadas con fecha real"]]} onTrace={onTrace} /></Box>{data.followups?.length > 0 && <Paper variant="outlined" sx={{
      p: 3,
      mt: 2
    }}><Typography variant="h6">A quién conviene acompañar</Typography><Typography variant="body2" color="text.secondary" mb={2}>Señales descriptivas basadas en registros. Confirma las barreras y acuerda el apoyo con cada participante.</Typography>{data.followups.map(f => <Stack key={f.participantId + ':' + f.activityId} direction={{
        xs: 'column',
        sm: 'row'
      }} gap={2} justifyContent="space-between" sx={{
        py: 1,
        borderBottom: 1,
        borderColor: 'divider'
      }}><Box><Typography fontWeight={600}>{f.name} · {f.activityName}</Typography><Typography variant="body2">{f.reason}</Typography></Box><Stack direction="row"><Button onClick={() => onTrace(f.refs)}>Ver evidencia</Button><Button onClick={() => onEdit('result', {
            projectId: data.project.id,
            activityId: f.activityId,
            participantId: f.participantId,
            name: 'Seguimiento · ' + f.name,
            stage: f.stage,
            observedOn: data.filters.asOf
          })}>Registrar observación</Button></Stack></Stack>)}</Paper>}<Alert severity="info" sx={{
      mt: 2
    }}>Aprendizaje, aplicación y resultado externo se registran por separado. Una persona puede tener logros en varias actividades; no se suman como personas únicas. Estas series describen observaciones, no prueban causalidad ni una tendencia estadística con pocas semanas.</Alert></>;
}
export default function ProjectVisuals({
  projectId,
  project,
  records,
  revision,
  busy,
  onEdit,
  execute
}) {
  const [view, setView] = useState('gantt'),
    [activityId, setActivityId] = useState(''),
    [from, setFrom] = useState(project.startDate),
    [to, setTo] = useState(project.endDate),
    [asOf, setAsOf] = useState(() => new Intl.DateTimeFormat('en-CA', {
      timeZone: 'America/La_Paz'
    }).format(new Date())),
    [baselineId, setBaselineId] = useState(''),
    [data, setData] = useState(null),
    [error, setError] = useState(''),
    [trace, setTrace] = useState(null),
    [approval, setApproval] = useState(false),
    [approvalName, setApprovalName] = useState('Plan inicial'),
    [approvalNote, setApprovalNote] = useState(''),
    [reload, setReload] = useState(0);
  const pending = useRef(null);
  const requestSequence = useRef(0);
  useEffect(() => {
    let cancelled = false;
    const sequence = ++requestSequence.current;
    setData(null);
    setError('');
    pmApi.command('pm_dashboard', {
      projectId,
      from,
      to,
      asOf,
      ...(activityId ? {
        activityId
      } : {}),
      ...(baselineId ? {
        baselineId
      } : {})
    }).then(result => {
      if (!cancelled && sequence === requestSequence.current) {
        setData(result.views);
        if (!result.views) setError('El backend aún no devuelve las vistas PM. Actualiza frontend y backend juntos.');
      }
    }).catch(e => {
      if (!cancelled) setError(e.response?.data?.message || 'No se pudieron cargar las vistas.');
    });
    return () => {
      cancelled = true;
    };
  }, [projectId, revision, activityId, from, to, asOf, baselineId, reload]);
  const activities = (records.activity || []).filter(a => !a.archived && a.projectId === projectId);
  const send = async (tool, args) => {
    pending.current = {
      tool,
      args
    };
    try {
      await execute(tool, args);
      pending.current = null;
      setError('');
      setReload(n => n + 1);
      return true;
    } catch (e) {
      setError(e.response?.data?.message || 'No se confirmó el envío. Reintenta el mismo envío.');
      return false;
    }
  };
  const move = (task, status) => {
    if (status === task.status) return;
    if (status === 'completed') {
      onEdit('task', {
        ...task,
        status
      });
      return;
    }
    send('pm_save_record', {
      kind: 'task',
      recordId: task.id,
      data: {
        status
      },
      expectedRevision: revision,
      requestId: crypto.randomUUID()
    });
  };
  const approve = async e => {
    e.preventDefault();
    const ok = await send('pm_save_record', {
      kind: 'baseline',
      data: {
        projectId,
        name: approvalName,
        approvalNote
      },
      expectedRevision: revision,
      requestId: crypto.randomUUID()
    });
    if (ok) {
      setApproval(false);
      setBaselineId('');
    }
  };
  const edit = (kind, r) => {
    const record = r ? (records[kind] || []).find(x => x.id === r.id) || r : undefined;
    onEdit(kind, record);
  };
  return <Box className="pm-visuals"><Paper variant="outlined" sx={{
      p: 3,
      mb: 3
    }}><Stack direction={{
        xs: 'column',
        md: 'row'
      }} gap={2} justifyContent="space-between"><Box><Typography variant="overline" color="primary">PM STAR · PLANIFICACIÓN Y BENEFICIOS</Typography><Typography variant="h5">Un plan, cinco formas de decidir</Typography><Typography color="text.secondary" mt={1}>{project.objective}</Typography></Box><Stack direction="row" gap={1} alignItems="center"><Button variant="outlined" disabled={busy} onClick={() => setApproval(true)}>Aprobar plan</Button><Button onClick={() => edit('task')}>Nueva tarea</Button></Stack></Stack><Box className="pm-visual-filters" mt={3}><TextField label="Actividad en todas las vistas" select size="small" value={activityId} onChange={e => setActivityId(e.target.value)}><MenuItem value="">Todo el proyecto</MenuItem>{activities.map(a => <MenuItem key={a.id} value={a.id}>{a.name}</MenuItem>)}</TextField><TextField label="Desde" type="date" size="small" InputLabelProps={{
          shrink: true
        }} value={from} onChange={e => setFrom(e.target.value)} /><TextField label="Hasta" type="date" size="small" InputLabelProps={{
          shrink: true
        }} value={to} onChange={e => setTo(e.target.value)} /><TextField label="Datos reales hasta" type="date" size="small" InputLabelProps={{
          shrink: true
        }} value={asOf} onChange={e => setAsOf(e.target.value)} /></Box>{data && <Stack direction={{
        xs: 'column',
        sm: 'row'
      }} gap={2} mt={2} alignItems="center"><TextField label="Comparar con plan aprobado" size="small" select value={baselineId} onChange={e => setBaselineId(e.target.value)} sx={{
          minWidth: 250
        }}><MenuItem value="">{data.baselines.length ? 'Último plan aprobado' : 'Sin plan aprobado'}</MenuItem>{data.baselines.map(b => <MenuItem key={b.id} value={b.id}>{b.name} · {new Date(b.approvedOn).toLocaleDateString('es-BO', {
              timeZone: 'America/La_Paz'
            })}</MenuItem>)}</TextField><Typography variant="caption" color="text.secondary">Aprobar conserva una copia del plan completo. Reprogramar después no modifica esa copia.</Typography></Stack>}</Paper>
 {error && <Alert severity="error" sx={{
      mb: 2
    }} action={pending.current ? <Button disabled={busy} onClick={() => send(pending.current.tool, pending.current.args)}>Reintentar envío</Button> : <Button onClick={() => setReload(n => n + 1)}>Recargar</Button>}>{error}</Alert>}
 <Paper variant="outlined" sx={{
      mb: 2
    }}><Tabs value={view} onChange={(_, v) => setView(v)} variant="scrollable" scrollButtons="auto">{viewNames.map(([key, label]) => <Tab key={key} value={key} label={label} />)}</Tabs></Paper>
 {!data && !error ? <Box textAlign="center" p={4}><CircularProgress size={26} /><Typography mt={1}>Conectando cronograma, recursos y resultados…</Typography></Box> : data && <>{data.quality.length > 0 && <Alert severity="warning" sx={{
        mb: 2
      }}><Typography fontWeight={600}>Qué falta para interpretar el plan</Typography>{data.quality.map(q => <Typography variant="body2" key={q}>{q}</Typography>)}</Alert>}{view === 'gantt' ? <Gantt data={data} records={records} onFocus={setActivityId} onTrace={setTrace} /> : view === 'kanban' ? <Kanban data={data} busy={busy} onMove={move} onEdit={edit} /> : view === 'load' ? <LoadMap data={data} onTrace={setTrace} onEdit={edit} /> : view === 'variance' ? <Variance data={data} onTrace={setTrace} /> : <Trends data={data} onTrace={setTrace} onEdit={edit} />}<Typography variant="caption" color="text.secondary" display="block" mt={2}>Misma fuente y filtros para las cinco vistas · Revisión {revision} · Corte {asOf} · Moneda {project.currency}</Typography></>}
 <Dialog open={Boolean(trace)} onClose={() => setTrace(null)} maxWidth="sm" fullWidth><DialogTitle>Registros que respaldan esta vista</DialogTitle><DialogContent>{trace?.length ? trace.map((r, i) => <Paper variant="outlined" key={r.kind + ':' + r.id + ':' + i} sx={{
          p: 2,
          mb: 1
        }}><Typography fontWeight={600}>{r.name}</Typography><Typography variant="caption">{labels[r.kind]}{r.date ? ' · ' + formatDate(r.date) : ''}{r.planned !== undefined ? ' · asignación ' + r.planned : ''}</Typography>{r.sessionName && <Typography variant="body2">{r.sessionName}</Typography>}<Button size="small" onClick={() => {
            edit(r.kind, r);
            setTrace(null);
          }}>Abrir registro</Button></Paper>) : <Typography>No hay observaciones registradas para esta selección. Los datos ausentes no se sustituyen por cero.</Typography>}</DialogContent><DialogActions><Button onClick={() => setTrace(null)}>Cerrar</Button></DialogActions></Dialog>
 <Dialog open={approval} onClose={busy ? undefined : () => setApproval(false)} fullWidth maxWidth="sm"><Box component="form" onSubmit={approve}><DialogTitle>Aprobar y conservar el plan</DialogTitle><DialogContent><Stack gap={2} pt={1}><Alert severity="info">Se conservarán actividades, sesiones, recursos, asignaciones y tareas del proyecto completo. Cada nueva aprobación requiere un motivo; las versiones anteriores permanecen disponibles.</Alert><TextField label="Nombre de esta versión" required value={approvalName} onChange={e => setApprovalName(e.target.value)} inputProps={{
              maxLength: 160
            }} /><TextField label="Motivo y supuestos de aprobación" multiline minRows={3} required value={approvalNote} onChange={e => setApprovalNote(e.target.value)} inputProps={{
              maxLength: 2000
            }} />{error && <Alert severity="error">{error}</Alert>}</Stack></DialogContent><DialogActions><Button disabled={busy} onClick={() => setApproval(false)}>Cerrar</Button><Button type="submit" variant="contained" disabled={busy}>Conservar plan aprobado</Button></DialogActions></Box></Dialog>
 </Box>;
}
